import { Injectable, computed, signal } from '@angular/core';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { RecordRef } from '../../ai/features/record-ref';

/**
 * A piece of context a screen "plugs in" for Nova. One per kind of host screen
 * (meeting, record-profile). The harness only ever reads `active*` projections.
 */
export type NovaContext =
  | { kind: 'meeting'; activity: AppointmentActivity; grounding?: () => string }
  | { kind: 'record';  record: RecordRef;             grounding?: () => string };

export type NovaRecordContext = Extract<NovaContext, { kind: 'record' }>;

/**
 * Mediator/registry between screens and the global Nova harness. Screens push
 * context via a lifecycle directive; the harness only ever reads `active` /
 * `activeMeeting` / `activeRecord`. The harness never knows about any specific page.
 */
@Injectable({ providedIn: 'root' })
export class NovaContextService {
  readonly #stack = signal<NovaContext[]>([]);

  /** Topmost plugged context, or null when nothing is plugged. */
  readonly active = computed<NovaContext | null>(() => this.#stack().at(-1) ?? null);

  /** Convenience: the active meeting activity, or null. */
  readonly activeMeeting = computed<AppointmentActivity | null>(() => {
    const ctx = this.active();
    return ctx?.kind === 'meeting' ? ctx.activity : null;
  });

  /** Convenience: the active record (contact/account/spotlight), or null. */
  readonly activeRecord = computed<RecordRef | null>(() => {
    const ctx = this.active();
    return ctx?.kind === 'record' ? ctx.record : null;
  });

  /**
   * All currently-plugged contexts, most-recent first, de-duplicated by identity
   * (a screen that re-plugs the same meeting/record collapses to one entry). The
   * harness surfaces these so the rep can switch which one's Insights they view
   * and attach any of them — rather than only ever seeing the topmost.
   */
  readonly activeContexts = computed<NovaContext[]>(() => {
    const seen = new Set<string>();
    const out: NovaContext[] = [];
    for (const c of [...this.#stack()].reverse()) {
      const key = c.kind === 'meeting' ? `m:${c.activity.ID}` : `r:${c.record.kind}:${c.record.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  });

  plug(ctx: NovaContext): void {
    this.#stack.update(s => [...s, ctx]);
  }

  unplug(ctx: NovaContext): void {
    this.#stack.update(s => s.filter(c => c !== ctx));
  }
}
